import { initialize } from 'launchdarkly-react-client-sdk';
import { LAUNCHDARKLY_CONFIG } from './constants';

class LaunchDarklyClient {
  constructor() {
    this.client = null;
    this.isInitialized = false;
    this.initializationPromise = null;
  }

  /**
   * Initialize the LaunchDarkly client
   * @param {Object} userContext - User context for LaunchDarkly
   * @returns {Promise} Promise that resolves when client is initialized
   */
  async initialize(userContext = null) {
    if (this.isInitialized) {
      return this.client;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._createClient(userContext);
    return this.initializationPromise;
  }

  /**
   * Create and initialize the LaunchDarkly client
   * @param {Object} userContext - User context for LaunchDarkly
   * @returns {Promise} Promise that resolves with the initialized client
   */
  async _createClient(userContext) {
    try {
      const clientSideId = LAUNCHDARKLY_CONFIG.CLIENT_SIDE_ID;

      if (!clientSideId) {
        console.warn(
          'LaunchDarkly client-side ID not found. Feature flags will be disabled.'
        );
        this.isInitialized = true;
        return null;
      }

      // Use provided user context or default to anonymous
      const context = userContext || LAUNCHDARKLY_CONFIG.ANONYMOUS_USER;
      this.client = initialize(clientSideId, context);

      // Wait for client to be ready
      await this.client.waitForInitialization(
        parseInt(LAUNCHDARKLY_CONFIG.INITIALIZATION_TIMEOUT_SECONDS)
      );

      this.isInitialized = true;

      return this.client;
    } catch (error) {
      console.error('Failed to initialize LaunchDarkly client:', error);
      this.isInitialized = false;
      this.initializationPromise = null;
      return null;
    }
  }

  /**
   * Get the current LaunchDarkly client instance
   * @returns {Object|null} LaunchDarkly client or null if not initialized
   */
  getClient() {
    return this.client;
  }

  /**
   * Check if the client is initialized
   * @returns {boolean} True if client is initialized
   */
  isClientInitialized() {
    return this.isInitialized && this.client !== null;
  }

  /**
   * Close the LaunchDarkly client connection
   */
  async close() {
    if (this.client) {
      try {
        await this.client.close();
        this.client = null;
        this.isInitialized = false;
        this.initializationPromise = null;
        console.log('LaunchDarkly client closed');
      } catch (error) {
        console.error('Failed to close LaunchDarkly client:', error);
      }
    }
  }
}

// Create a singleton instance
const launchDarklyClient = new LaunchDarklyClient();

export default launchDarklyClient;
